import { CURRENT_ENV } from "./confighelpers";

/**
 * Club-owner onboarding order (new):
 *   sign up -> verify phone -> choose plan (Stripe) -> club details wizard -> dashboard
 *   (Google / Apple sign-ups follow the same order since 2026-10-28; they skip the phone code and type the club phone
 *   in the club details wizard. The old wizard at /on-bording was removed and now redirects to /start/plan.)
 *
 * users.onBoardingStep:
 *   0..2 = not subscribed yet        -> /start/plan
 *   3    = subscribed (set by the Stripe webhook via updateUserSubscription)
 *   4    = club details completed after subscribing
 * Clubs that onboarded with the old order sit at 3 with a business profile, so they are treated as done.
 */
export const ONBOARDING_ROUTES = {
  plan: "/start/plan",
  checkout: "/start/checkout", /* Stripe checkout for the plan chosen on /start/plan (?plan=<id>) */
  welcome: "/start/welcome",
  club: "/start/club",
  payouts: "/start/payouts",
};

export const OWNER_BUSINESS_TYPE = "class";

/**
 * The club's Stripe payment account is connected and onboarded. Members are created as customers in this
 * account (backend addMembers / product-migration only do that when this is true), so adding, inviting
 * and importing members needs it.
 */
export const hasConnectedStripe = (userData) => {
  const d = userData?.myConnectedPaymentProfiles?.stripe?.profileDetails || {};
  return !!d.stripeAcId && d.stripeOnBoardindStatus === "SUCCESS";
};
export const STRIPE_SETUP_ROUTE = "/my-account/online-payment";

export const isSubscribedOwner = (userData) => (userData?.onBoardingStep || 0) >= 3;

export const hasClubDetails = (userData) => {
  if ((userData?.onBoardingStep || 0) >= 4) return true;
  const profile = userData?.myBusinessProfile || {};
  // Only real values count: the users schema fills defaults such as address.addressType = "manually"
  return (
    !!(profile.businessName && String(profile.businessName).trim()) ||
    !!(profile.address && (profile.address.address || profile.address.addressName)) ||
    !!userData?.schoolObj?.schoolName
  );
};

/**
 * Where a signed-in club owner should go next during onboarding, or null when onboarding is finished.
 */
export const getOnboardingRedirect = (userData) => {
  if (!userData || userData.userType === "student") return null;
  const step = userData.onBoardingStep || 0;
  if (step < 3) {
    if (userData.isVerified === false) return null;
    return ONBOARDING_ROUTES.plan;
  }
  if (!hasClubDetails(userData)) return ONBOARDING_ROUTES.welcome;
  return null;
};

/** Stripe publishable keys are public by design (platform account) */
const STRIPE_PUBLISHABLE_KEYS = {
  prod: "pk_live_51JzKMtDx1lYwRllZ7SopvqoyzlVjtwOrFnal03xTL4NHxRG87ZCZ15Si7hpCCjYlaKvrygZy3Zm94XZ7ALItOvZH00Kxb1Fpni",
  test: "pk_test_51JzKMtDx1lYwRllZz3E8uV5JH029a7V2fIkTcSOCk1Ww4qz7XUC7CmOsHvUVPilicCNxn926gw2aKLD2iuzVykfk00EZWvIdMW",
};

export const getStripePublishableKey = () =>
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ||
  (CURRENT_ENV === "prod" ? STRIPE_PUBLISHABLE_KEYS.prod : STRIPE_PUBLISHABLE_KEYS.test);

let stripePromise = null;
/** Loads Stripe.js from js.stripe.com once (no npm dependency needed) */
export const loadStripeJs = () => {
  if (window.Stripe) return Promise.resolve(window.Stripe(getStripePublishableKey()));
  if (!stripePromise) {
    stripePromise = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = "https://js.stripe.com/v3/";
      script.async = true;
      script.onload = () => {
        if (typeof window.Stripe !== "function") {
          stripePromise = null;
          reject(new Error("Could not load Stripe"));
          return;
        }
        resolve(window.Stripe(getStripePublishableKey()));
      };
      script.onerror = () => {
        stripePromise = null;
        reject(new Error("Could not load Stripe"));
      };
      document.head.appendChild(script);
    });
  }
  return stripePromise;
};

/** First-time dashboard tour: requested when onboarding finishes, shown once per user */
const TOUR_PENDING_KEY = "fcDashboardTourPending";
const TOUR_DONE_PREFIX = "fcDashboardTourDone_";

export const markDashboardTourPending = () => {
  try { localStorage.setItem(TOUR_PENDING_KEY, "1"); } catch (e) { /* storage unavailable */ }
};

/**
 * Shown once per club owner (per browser) when:
 *   - onboarding just asked for it (Payouts: "Go to my dashboard" / "Set up later"), or
 *   - the club finished the new onboarding (onBoardingStep 4, set when the club details are saved) and hasn't seen it.
 *     (2026-10-28: the tour didn't show when the dashboard was reached another way, e.g. straight from the welcome
 *     page or after a reload; clubs from the old onboarding sit at step 3 and don't get it.)
 * ?tour=1 on the dashboard always shows it.
 */
export const shouldShowDashboardTour = (userId, userData = null) => {
  try {
    if (new URLSearchParams(window.location.search).get("tour") === "1") return true;
    if (!userId || localStorage.getItem(TOUR_DONE_PREFIX + userId) === "1") return false;
    if (localStorage.getItem(TOUR_PENDING_KEY) === "1") return true;
    return !!userData && userData.userType !== "student" && (userData.onBoardingStep || 0) >= 4;
  } catch (e) {
    return false;
  }
};

export const markDashboardTourDone = (userId) => {
  try {
    localStorage.removeItem(TOUR_PENDING_KEY);
    localStorage.setItem(TOUR_DONE_PREFIX + (userId || ""), "1");
  } catch (e) { /* storage unavailable */ }
};
